import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { ShieldAlert, Globe, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate, Modal } from './adminUtils';

export const SecurityTab = () => {
  const [securityAlerts, setSecurityAlerts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ipHistoryUser, setIpHistoryUser] = useState(null);
  const [ipHistory, setIpHistory] = useState(null);
  const [ipHistoryLoading, setIpHistoryLoading] = useState(false);

  const fetchSecurityAlerts = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/security-alerts`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setSecurityAlerts(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSecurityAlerts(); }, []);

  const fetchIpHistory = async (email) => {
    setIpHistoryUser(email); setIpHistory(null); setIpHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/user-ip-history/${encodeURIComponent(email)}`, { headers: { 'Authorization': `Bearer ${getAdminToken()}` } });
      if (res.ok) setIpHistory(await res.json());
    } catch (e) { toast.error('Failed to load IP history'); }
    finally { setIpHistoryLoading(false); }
  };

  return (
    <>
      <div className="space-y-4">
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-red-400" />Security Alerts
                {securityAlerts && securityAlerts.high > 0 && <Badge className="bg-red-500/20 text-red-300 animate-pulse">{securityAlerts.high} High</Badge>}
              </CardTitle>
              <Button variant="outline" size="sm" onClick={fetchSecurityAlerts} className="border-white/20 text-white"><RefreshCw className="w-4 h-4 mr-1" />Scan</Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-red-500 animate-spin" /></div> : securityAlerts ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center"><p className="text-xs text-red-300">High Risk</p><p className="text-2xl font-bold text-white">{securityAlerts.high}</p></div>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center"><p className="text-xs text-amber-300">Medium</p><p className="text-2xl font-bold text-white">{securityAlerts.medium}</p></div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-center"><p className="text-xs text-blue-300">Low</p><p className="text-2xl font-bold text-white">{securityAlerts.low}</p></div>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center"><p className="text-xs text-emerald-300">Total Alerts</p><p className="text-2xl font-bold text-white">{securityAlerts.total}</p></div>
                </div>

                {securityAlerts.alerts.length === 0 ? (
                  <div className="text-center py-8"><CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" /><p className="text-emerald-400 font-medium">All Clear</p><p className="text-slate-500 text-sm">No suspicious activity detected in last 24 hours</p></div>
                ) : (
                  <div className="space-y-2">
                    {securityAlerts.alerts.map((alert, i) => (
                      <div key={i} className={`rounded-xl p-4 border ${alert.severity === 'high' ? 'bg-red-500/5 border-red-500/20 border-l-2 border-l-red-500' : alert.severity === 'medium' ? 'bg-amber-500/5 border-amber-500/20 border-l-2 border-l-amber-500' : 'bg-blue-500/5 border-blue-500/20 border-l-2 border-l-blue-400'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={alert.severity === 'high' ? 'bg-red-500/20 text-red-300' : alert.severity === 'medium' ? 'bg-amber-500/20 text-amber-300' : 'bg-blue-500/20 text-blue-300'}>{alert.severity.toUpperCase()}</Badge>
                              <Badge variant="outline" className="text-slate-400 border-white/20 text-xs">
                                {alert.type === 'multiple_ips' ? 'Multiple IPs' : alert.type === 'shared_ip' ? 'Shared IP' : alert.type === 'brute_force' ? 'Brute Force' : 'Multiple VMs'}
                              </Badge>
                            </div>
                            {alert.email && <p className="text-white font-medium">{alert.email}</p>}
                            {alert.ip && <p className="text-slate-400 text-sm font-mono">{alert.ip}</p>}
                            <p className="text-slate-400 text-sm mt-1">{alert.detail}</p>
                            {alert.ips && <div className="flex flex-wrap gap-1 mt-2">{alert.ips.map((ip, j) => <span key={j} className="text-xs font-mono bg-white/5 rounded px-2 py-0.5 text-slate-400">{ip}</span>)}</div>}
                            {alert.emails && <div className="flex flex-wrap gap-1 mt-2">{alert.emails.map((em, j) => <span key={j} className="text-xs bg-white/5 rounded px-2 py-0.5 text-slate-400">{em}</span>)}</div>}
                          </div>
                          {alert.email && (
                            <Button size="sm" variant="outline" onClick={() => fetchIpHistory(alert.email)} className="border-white/20 text-slate-300 ml-2" title="View IP History">
                              <Globe className="w-3 h-3 mr-1" />History
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-xs text-slate-600 text-right">Last scanned: {securityAlerts.checked_at ? formatDate(securityAlerts.checked_at) : '-'}</p>
              </div>
            ) : <p className="text-slate-400 text-center py-8">Click "Scan" to check for suspicious activity</p>}
          </CardContent>
        </Card>
      </div>

      {/* IP History Modal */}
      <Modal show={!!ipHistoryUser} onClose={() => { setIpHistoryUser(null); setIpHistory(null); }} title={`IP History: ${ipHistoryUser || ''}`}>
        {ipHistoryLoading ? <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 text-red-500 animate-spin" /></div> : ipHistory ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/5 rounded-lg p-3 text-center"><p className="text-xs text-slate-400">Unique IPs</p><p className="text-xl font-bold text-white">{ipHistory.total_ips}</p></div>
              <div className="bg-white/5 rounded-lg p-3 text-center"><p className="text-xs text-slate-400">Total Logins</p><p className="text-xl font-bold text-white">{ipHistory.total_logins}</p></div>
              <div className="bg-white/5 rounded-lg p-3 text-center"><p className="text-xs text-slate-400">Period</p><p className="text-lg font-bold text-white">30 days</p></div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-2">IP Addresses</h3>
              <div className="space-y-2">
                {ipHistory.unique_ips.map((ip, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-white font-mono">{ip.ip}</p>
                      <p className="text-xs text-slate-500">First: {formatDate(ip.first_seen)} | Last: {formatDate(ip.last_seen)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold">{ip.count}x</p>
                      <div className="flex gap-2 text-xs">
                        <span className="text-emerald-400">{ip.success} ok</span>
                        {ip.failed > 0 && <span className="text-red-400">{ip.failed} fail</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-2">Recent Logins</h3>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {ipHistory.recent_logins.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-white/5">
                    <div className="flex items-center gap-2">
                      {l.success ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                      <span className="text-slate-300 font-mono text-xs">{l.ip}</span>
                    </div>
                    <span className="text-slate-500 text-xs">{formatDate(l.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : <p className="text-slate-400">No data</p>}
      </Modal>
    </>
  );
};
